import React from 'react'
import map from 'lodash/map'
import times from 'lodash/times'
import find from 'lodash/find'
import classNames from 'classnames'
import Icon from '../shared/icon'
import { PlateContainer, PlateReplenishment, ReplenishmentOrder, HUMAN_SLOTS } from './types'
import { t } from "../../i18n";

const PLATE_CONTAINER_SLOT_COUNT = 2

interface PlateContainerSlotProps {
  slotNumber: number,
  humanSlotNumber: string,
  container: PlateContainer,
  expandPlateContainer: Function,
  replenishmentView?: boolean,
  replenishedSlot?: PlateReplenishment,
  isDisabled?: boolean,
  isFaulty?: boolean,
  activeReplenishmentOrders: ReplenishmentOrder[]
}

const AmountBar = ({ replenishedSlot, replenishmentView, maxAmount, remainingAmount, replenishedAmount, amountStatus,  }) => {

  const maxAmountBarSteps = 25

  const stepsFor = (amount: number) => maxAmount > 0 ? Math.ceil(amount * (maxAmountBarSteps / maxAmount)) : 0

  const remainingBarSteps = stepsFor(remainingAmount)

  let replenishedSteps = stepsFor(replenishedAmount)
  const emptyBarSteps = maxAmountBarSteps - (remainingBarSteps + replenishedSteps)

  if(emptyBarSteps < 0) {
    replenishedSteps = replenishedSteps + emptyBarSteps // since Ceil is used for calculation it might happen that there is 1 step too much
  }

  return (
    <>
      {(!!replenishedSlot && replenishmentView) ?
        <>
          <div className={`amount-bar status-replenished`}>
            {times(emptyBarSteps, i =>
              <div key={i} className='amount-bar-step replenished-empty'></div>
            )}
          </div>

          <div className={`amount-bar status-replenished`}>
            {times(replenishedSteps, i =>
              <div key={i} className='amount-bar-step'></div>
            )}
          </div>

          <div className={`amount-bar status-${amountStatus}`}>
            {times(remainingBarSteps, i =>
              <div key={i} className='amount-bar-step'></div>
            )}
          </div>
        </> :
        <div className={`amount-bar status-${amountStatus}`}>
          {times(maxAmountBarSteps, i =>
            <div key={i} className={`amount-bar-step ${i < (maxAmountBarSteps - remainingBarSteps) ? 'empty' : ''}`}></div>
          )}
        </div>
      }
    </>
  )
}

const PlateContainerSlot: React.FC<PlateContainerSlotProps> = ({
  slotNumber, humanSlotNumber, container, expandPlateContainer,
  replenishmentView, replenishedSlot, isDisabled, isFaulty, activeReplenishmentOrders
}) => {

  const maxAmount = container?.max_amount || 0
  const remainingAmount = container?.remaining_amount || 0
  // A plate replenishment records the bowls to be delivered, not the new total.
  const replenishedAmount = replenishedSlot ? replenishedSlot.amount : 0

  const amountStatus = container?.remaining_amount_status || 'alert'

  const containerClassNames = classNames(
    'plate-container',
    {
      'replenishment': replenishmentView,
      'replenished': !!replenishedSlot && replenishmentView,
      'disabled': isDisabled,
      'faulty': isFaulty,
    }
  )

  const hasSentReplenishment = () => {
    return !!find(activeReplenishmentOrders, order =>
      !!find(order.plateReplenishments, repl => repl.slotNumber == slotNumber && repl.state !== 'canceled')
    )
  }


  if(isDisabled) {
    return (
      <div className={containerClassNames}>
        <div className="edit-plate-container" style={{marginTop: 'auto'}}>
          <div className='container-name mb-3'>(disabled)</div>
          <div className='plate-icon'>
            <Icon name='plate' color={(!!replenishedSlot && replenishmentView) ? '#CA56F3' : "#000000"}/>
          </div>
          <div className='container-name'>{`Stand #${humanSlotNumber}`}</div>
        </div>
      </div>
    )
  }
  return (
    <div className={containerClassNames}  onClick={() => expandPlateContainer(slotNumber)}>
        {hasSentReplenishment() && replenishmentView &&
          <span className="replenishment-sent-corner-status"></span>
        }
      <AmountBar
        replenishedSlot={replenishedSlot}
        replenishmentView={replenishmentView}
        maxAmount={maxAmount}
        remainingAmount={remainingAmount}
        replenishedAmount={replenishedAmount}
        amountStatus={amountStatus}
      />
      <div className='amount'>
        <div className='remaining-amount'>
          <span className={`status-${amountStatus}`}>{remainingAmount}</span>
          <span className='status-replenished'> {(!!replenishedSlot && replenishmentView) ? ` + ${replenishedAmount}` : ''}</span>
          <span>{` / ${maxAmount}`}</span>
        </div>
        <div>{t('recipes.bowls')}</div>
      </div>
      <div className="edit-plate-container">
        <div className='plate-icon'>
          <Icon name='plate' color={(!!replenishedSlot && replenishmentView) ? '#CA56F3' : "#000000"}/>
        </div>
        <div className='container-name'>{`Stand #${humanSlotNumber}`}</div>
        {isFaulty && <div className='plate-faulty-label'>faulty</div>}
      </div>
    </div>
  )
}
interface PlatesViewProps {
  plateContainers: Record<number, PlateContainer>,
  expandPlateContainer: Function,
  replenishmentView: boolean,
  draftReplenishmentOrder: ReplenishmentOrder,
  disabledSlotNumbers: number[],
  faultySlotNumbers?: number[],
  isSelectedTab: boolean,
  activeReplenishmentOrders: ReplenishmentOrder[]
}

const PlatesView: React.FC<PlatesViewProps> = ({
  plateContainers, expandPlateContainer, replenishmentView,
  draftReplenishmentOrder, disabledSlotNumbers, faultySlotNumbers, isSelectedTab, activeReplenishmentOrders
}) => {

  const plateSlots = map(Array.from(Array(PLATE_CONTAINER_SLOT_COUNT).keys()), slotIndex => {
    const slotNumber = slotIndex + 1

    return { slotNumber, container: plateContainers[slotNumber] }
  })

  const replenishedSlot = (slot) => {
    return find(draftReplenishmentOrder.plateReplenishments, repl => repl.slotNumber == slot)
  }

  return (
    <div className='tab-content plates' style={{maxWidth: isSelectedTab ? '100%' : '0'}}>
      <div className='plates-wrapper'>
        {map(plateSlots, (slot, index) => (
          <PlateContainerSlot
            key={slot.slotNumber}
            slotNumber={slot.slotNumber}
            humanSlotNumber={slot.container?.human_slot_number || HUMAN_SLOTS[index]}
            container={slot.container}
            expandPlateContainer={expandPlateContainer}
            replenishmentView={replenishmentView}
            replenishedSlot={replenishedSlot(slot.slotNumber)}
            isDisabled={disabledSlotNumbers?.includes(slot.slotNumber)}
            isFaulty={faultySlotNumbers?.includes(slot.slotNumber)}
            activeReplenishmentOrders={activeReplenishmentOrders}
          />
          )
        )}
      </div>
    </div>
  )
}

export default PlatesView
